import React from 'react';
import { MIN_COLUMN_WIDTH } from './constants';
import { CELL_PADDING, HEADER_EXTRA, createTextMeasurer } from '../../../utils/measureText';

const applyWidth = (tableDispatch, column, targetWidth) => {
    const startWidth = column.totalWidth;
    tableDispatch({
        type: 'columnStartResizing',
        columnId: column.id,
        columnWidth: startWidth,
        headerIdWidths: [[column.id, startWidth]],
        clientX: 0,
    });
    tableDispatch({ type: 'columnResizing', clientX: targetWidth - startWidth });
    tableDispatch({ type: 'columnDoneResizing' });
};

/**
 * Reads the text actually rendered in a column's cell for `row`, or null when
 * the cell is not in the DOM (off-page rows, or unit tests with no rendered
 * table). `tableType` reproduces the cell id set by TableCore/ReportsTable:
 * `${tableType}Cell_${columnId}-${rowId}`.
 *
 * The lookup is scoped to `root` (the table's own wrapper) rather than the whole
 * document: two tables sharing a tableType (or two ReportsTables) render cells
 * with identical ids, so a document-wide getElementById could measure the wrong
 * table's cell and mis-size this column. Falls back to `document` only when no
 * wrapper is available (unit tests with no rendered table).
 *
 * Auto-fit must measure what the user sees. NAME/STATUS/TYPE/USER derive their
 * displayed text from the device mapping + i18n (e.g. DeviceName renders
 * `t(deviceMapping.label)`), so the raw accessor `row.values[column.id]` is
 * null or a code and does not reflect the visible name. `textContent` returns
 * the full rendered text regardless of CSS ellipsis truncation (PM-21997).
 */
const renderedCellText = (root, tableType, columnId, rowId) => {
    if (rowId == null) return null;
    const scope = root || (typeof document !== 'undefined' ? document : null);
    if (!scope) return null;
    const cell = scope.querySelector(`[id="${tableType}Cell_${columnId}-${rowId}"]`);
    const text = cell?.textContent?.trim();
    return text || null;
};

/**
 * Double-click auto-fit (PM-19894) and keyboard resize (PM-19895) actions,
 * shared by TableCore and ReportsTableWithData so both tables behave
 * identically (PM-18153 AC: "consistent across all main tables").
 *
 * Both paths clamp to MIN_COLUMN_WIDTH — the same minimum react-table applies
 * to drag-resize via `defaultColumn` (PM-18398).
 */
export function useColumnResizeActions({ rows, tableDispatch, tableType = '', containerRef }) {
    const handleAutoFit = React.useCallback((column) => {
        const measure = createTextMeasurer();
        // Scope the cell lookup to this table's wrapper so a sibling table with
        // the same cell ids can't be measured by mistake (see renderedCellText).
        const root = containerRef?.current || null;

        const headerText = typeof column.Header === 'string' ? column.Header : column.id;
        let widest = measure(headerText) + HEADER_EXTRA;

        (rows || []).forEach((row) => {
            // Prefer the rendered cell text so derived columns (NAME/STATUS/TYPE/
            // USER) auto-fit to the visible name instead of collapsing to the
            // header — the raw accessor is null/a code for those (PM-21997).
            let text = renderedCellText(root, tableType, column.id, row?.id);
            if (text == null) {
                // Fallback: the raw accessor value. Correct when the cell renders
                // it verbatim (e.g. NICKNAME), and the only value available for
                // off-page rows or in hook unit tests with no rendered DOM.
                const value = row.values?.[column.id];
                if (value == null) return;
                text = Array.isArray(value) ? value.join(', ') : value;
            }
            widest = Math.max(widest, measure(text) + CELL_PADDING);
        });

        // An empty column still auto-fits to at least the shared minimum.
        applyWidth(tableDispatch, column, Math.max(Math.ceil(widest), MIN_COLUMN_WIDTH));
    }, [rows, tableDispatch, tableType, containerRef]);

    const handleKeyboardResize = React.useCallback((column, delta) => {
        const targetWidth = Math.max(column.totalWidth + delta, MIN_COLUMN_WIDTH);
        applyWidth(tableDispatch, column, targetWidth);
    }, [tableDispatch]);

    return { handleAutoFit, handleKeyboardResize };
}
